import {
  BackendCompatibleLocales,
  EventStatus,
  AttendeeRequiredFieldsType,
} from '../../enums';
import { z } from 'zod';
import { AttendeeRequiredField } from '../../types/entities/event.entity';
import dayjs, { Dayjs } from 'dayjs';
import {
  getTranslationValidation,
  getTranslationValidationWithLimit,
} from '../../helpers';
import { TranslationValidatorSchema } from './translation.validator';

// `enforceFutureStartDate` is only true when creating an event — a new event cannot start in the
// past. Editing an existing event must NOT re-apply that rule: an already-started (or past) event is
// legitimately editable, and forcing its start date into the future would block every save. See
// EventEditFormValidatorSchema.
const makeEventFormValidatorDateSchema = (enforceFutureStartDate: boolean) => {
  const schema = z
    .object({
      // The date picker starts empty, so form state holds `null` until the
      // user picks a date. `.nullable()` + refine keeps that as the *input*
      // shape while the validated output the submit handler receives is
      // always a Dayjs — the required-date rule is unchanged.
      startDate: z
        .instanceof(dayjs as unknown as typeof Dayjs, {
          message: 'event_error_start-date-required',
        })
        .nullable()
        .refine((value): value is Dayjs => value !== null, {
          message: 'event_error_start-date-required',
        }),
      endDate: z
        .instanceof(dayjs as unknown as typeof Dayjs)
        .optional()
        .nullable(),
    })
    .refine(
      (data) => {
        return data.endDate
          ? dayjs(data.endDate).isAfter(data.startDate)
          : true;
      },
      {
        message: 'event_error_invalid-end-date', // End date must be after the start date
        path: ['endDate'],
      }
    );

  if (!enforceFutureStartDate) {
    return schema;
  }

  return schema.refine(
    (data) => {
      return data.startDate ? dayjs(data.startDate).isAfter(new Date()) : true;
    },
    {
      message: 'event_error_invalid-start-date', // Start date must not be in the past
      path: ['startDate'],
    }
  );
};

const EventFormValidatorDateSchema = makeEventFormValidatorDateSchema(true);

/**
 * The stored enum plus the checkout-only `phoneNumber.localNumber` path. Typed
 * as {@link AttendeeRequiredField} so it yields the string literals the form
 * state and entities use, rather than the enum member types.
 */
const attendeeRequiredFieldSchema: z.ZodType<
  AttendeeRequiredField,
  AttendeeRequiredField
> = z.union([
  z.enum(AttendeeRequiredFieldsType),
  z.literal('phoneNumber.localNumber'),
]);

const eventFormValidatorFields = z.object({
  status: z.enum(EventStatus),
  shortName: getTranslationValidation(),
  description: getTranslationValidation(),
  name: getTranslationValidation(),
  slug: z.string().min(1),
  venueConfigId: z.string().min(1, 'venue_config_error_message'),
  imageUrl: z.any().optional(),
  /**
   * Form state can carry the checkout-only `phoneNumber.localNumber` path;
   * `prepareValues` strips it before submit because it is not a stored event
   * field. Accepting it here matches the form, not the payload.
   */
  attendeeRequiredFields: z
    .array(attendeeRequiredFieldSchema)
    .nullable()
    .optional(),
});

// required fields: name, shortName, description, startDate, slug, venueConfigId, status
export const EventFormValidatorSchema = z.intersection(
  EventFormValidatorDateSchema,
  eventFormValidatorFields
);

// Same as EventFormValidatorSchema but without the "start date must be in the future" rule, for the
// edit forms (company-settings/events/[id] and events/[id]), where the event may already have
// started. Everything else — required name/venue/etc. — is still enforced.
export const EventEditFormValidatorSchema = z.intersection(
  makeEventFormValidatorDateSchema(false),
  eventFormValidatorFields
);

// required fields: name, shortName, description, startDate, slug, venueConfigId, status

export const FGOEventFormValidatorSchema = z.intersection(
  EventFormValidatorDateSchema,
  z.object({
    shortName: getTranslationValidationWithLimit(
      255,
      'validation_name_too_long'
    ),
    description: getTranslationValidation(),

    ticketEvolutionId: z.string().min(1).optional(),
    attendeeRequiredFields: z
      .array(z.enum(AttendeeRequiredFieldsType))
      .optional(),
    imageFileId: z.any().optional(),
    venueConfigId: z.string().optional(),
    name: getTranslationValidationWithLimit(255, 'validation_name_too_long'),
  })
);
// required fields: name, shortName, description, startDate, slug, venueConfigId, status
export const FGOEventValidatorSchema = z.object({
  // Same translation entry as EventValidatorSchema: both describe the one event
  // create body, and declaring it two ways made every value that crossed between
  // the admin and FGO paths a type error.
  shortName: z.array(TranslationValidatorSchema),
  description: z.array(TranslationValidatorSchema),
  startDate: z.string(),
  endDate: z.string().optional().nullable(),
  ticketEvolutionId: z.string().min(1).optional().nullable(),
  attendeeRequiredFields: z
    .array(z.enum(AttendeeRequiredFieldsType))
    .optional()
    .nullable(),
  imageFileId: z.any().optional(),
  venueConfigId: z.string().optional(),
  /** Explicit scope for FGO event creation; both values are optional for legacy public events. */
  companyId: z.string().optional().nullable(),
  fanGroupId: z.string().optional().nullable(),
  name: z.array(
    z.object({ lang: z.enum(BackendCompatibleLocales), text: z.string() })
  ),
});

export const EventValidatorSchema = z.object({
  description: z.array(TranslationValidatorSchema),
  status: z.enum(EventStatus),
  shortName: z.array(TranslationValidatorSchema),
  slug: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  ticketEvolutionId: z.string().optional().nullable(),
  venueConfigId: z.string(),
  name: z.array(TranslationValidatorSchema),
  attendeeRequiredFields: z
    .array(z.enum(AttendeeRequiredFieldsType))
    .optional()
    .nullable(),
  imageFileId: z.any().optional(),
  /** Optional company ID when creating from company settings. Null/undefined means global admin. */
  companyId: z.string().optional().nullable(),
  /** Audience that should receive the EVENT_CREATED activity; without it none is recorded. */
  fanGroupId: z.string().optional().nullable(),
});

export type EventValidator = z.infer<typeof EventValidatorSchema>;
/** Validated output of the admin event create form (start date guaranteed). */
export type EventFormValidator = z.infer<typeof EventFormValidatorSchema>;
/** Validated output of the admin event edit forms. */
export type EventEditFormValidator = z.infer<typeof EventEditFormValidatorSchema>;
/** Validated output of the FGO event form (start date guaranteed). */
export type FGOEventFormValidator = z.infer<typeof FGOEventFormValidatorSchema>;
/** Form-state shape of the FGO event form (start date still unset). */
export type FGOEventFormValues = z.input<typeof FGOEventFormValidatorSchema>;
export type FGOEventValidator = z.infer<typeof FGOEventValidatorSchema>;
